import Vue from 'vue';
import { PushService } from '@/services/push/push.service';

// Estado do push remoto do app (STORY-1642). O registro do aparelho e os
// listeners nativos vivem em services/push/pushRegistration; aqui fica o que a
// UI lê: se o push funciona neste build, a permissão do SO, o estado dos canais
// Android e as preferências do aluno no backend.
const DEFAULT_PREFERENCES = () => ({ replies: true, newContent: true, mutedCourses: [] });

function normalizeMuted (list) {
  return Array.from(new Set((Array.isArray(list) ? list : [])
    .map(id => Number(id))
    .filter(id => Number.isInteger(id) && id > 0)));
}

function normalizePreferences (data) {
  const d = data && typeof data === 'object' ? data : {};
  return {
    replies: d.replies !== false,
    newContent: d.newContent !== false,
    mutedCourses: normalizeMuted(d.mutedCourses)
  };
}

function sameValue (a, b) {
  return Array.isArray(a) || Array.isArray(b)
    ? JSON.stringify(normalizeMuted(a)) === JSON.stringify(normalizeMuted(b))
    : a === b;
}

let _loadPromise = null;
// PUTs de preferência em fila: o menu do curso e Configurações podem disparar
// dois seguidos, e o mutedCourses de cada um é a lista COMPLETA. Fora de ordem,
// o mais antigo chegando por último desfaria o mais novo no servidor.
let _saveQueue = Promise.resolve();

export default {
  namespaced: true,
  state: {
    // null = ainda não sabemos; false = build sem Firebase configurado (ou fora
    // do app) — o push fica inativo e a UI some.
    available: null,
    // 'unknown' até a 1ª leitura; depois o que o serviço de permissão devolver.
    permission: 'unknown',
    channels: { content: null, replies: null },
    preferences: DEFAULT_PREFERENCES(),
    // O que o servidor confirmou por último (GET ou PUT com sucesso). É para cá
    // que a volta atrás de um PUT que falhou leva — não para a foto de antes do
    // pedido, que pode ser a mudança otimista de outro pedido que também falhou.
    confirmed: DEFAULT_PREFERENCES(),
    // studentId dono das preferências carregadas: troca de conta invalida o cache.
    preferencesOwner: null,
    preferencesLoaded: false,
    preferencesError: false,
    savingPreferences: false
  },
  getters: {
    isCourseMuted: state => courseId => state.preferences.mutedCourses.indexOf(Number(courseId)) !== -1,
    // Push que dá para usar na UI: app nativo e Firebase de pé neste build.
    isAvailable: state => state.available === true
  },
  actions: {
    loadPreferences ({ commit, state, rootState }, { force = false } = {}) {
      const owner = rootState.user.currentUser && rootState.user.currentUser.studentId;
      if (!owner) return Promise.resolve(null);
      if (!force && state.preferencesLoaded && state.preferencesOwner === owner) {
        return Promise.resolve(state.preferences);
      }
      if (_loadPromise) return _loadPromise;
      _loadPromise = PushService.getPreferences()
        .then(response => {
          // Resposta de outra conta (logout + login durante o voo) não vale.
          const now = rootState.user.currentUser && rootState.user.currentUser.studentId;
          if (now !== owner) return null;
          commit('SET_PREFERENCES', { owner, preferences: normalizePreferences(response.data) });
          return state.preferences;
        })
        .catch(error => {
          commit('SET_PREFERENCES_ERROR', true);
          throw error;
        })
        .finally(() => { _loadPromise = null; });
      return _loadPromise;
    },

    // Atualização otimista com volta atrás: o switch responde na hora e, se o
    // PUT falhar, desfaz e avisa. Os PUTs saem em fila (_saveQueue). A volta
    // atrás leva o campo ao último valor confirmado pelo servidor, e só se
    // ninguém mudou o campo depois deste pedido — restaurar a foto inteira
    // apagaria a mudança otimista de um pedido seguinte.
    updatePreferences ({ commit, state }, patch) {
      const body = {};
      if (typeof patch.replies === 'boolean') body.replies = patch.replies;
      if (typeof patch.newContent === 'boolean') body.newContent = patch.newContent;
      if (Array.isArray(patch.mutedCourses)) body.mutedCourses = normalizeMuted(patch.mutedCourses);
      if (!Object.keys(body).length) return Promise.resolve(true);

      commit('PATCH_PREFERENCES', body);

      const run = _saveQueue.then(async () => {
        commit('SET_SAVING', true);
        try {
          await PushService.updatePreferences(body);
          commit('CONFIRM_PREFERENCES', body);
          return true;
        } catch (error) {
          const revert = {};
          Object.keys(body).forEach(key => {
            if (sameValue(state.preferences[key], body[key])) revert[key] = state.confirmed[key];
          });
          if (Object.keys(revert).length) commit('PATCH_PREFERENCES', revert);
          commit('toast/NEW', {
            type: 'error',
            message: 'Não conseguimos salvar sua preferência de notificações agora. Tente de novo em instantes.',
            suppressOffline: true
          }, { root: true });
          return false;
        } finally {
          commit('SET_SAVING', false);
        }
      });
      _saveQueue = run.catch(() => {});
      return run;
    },

    // Silenciar curso (N5). `mutedCourses` no PUT é a lista COMPLETA, então
    // garante as preferências carregadas antes de montar a lista nova — senão um
    // silenciar feito antes do GET apagaria os outros cursos silenciados.
    async muteCourse ({ dispatch, state }, courseId) {
      const id = Number(courseId);
      if (!Number.isInteger(id) || id <= 0) return false;
      try { await dispatch('loadPreferences'); } catch (e) { return false; }
      if (state.preferences.mutedCourses.indexOf(id) !== -1) return true;
      return dispatch('updatePreferences', { mutedCourses: state.preferences.mutedCourses.concat(id) });
    },

    async unmuteCourse ({ dispatch, state }, courseId) {
      const id = Number(courseId);
      try { await dispatch('loadPreferences'); } catch (e) { return false; }
      if (state.preferences.mutedCourses.indexOf(id) === -1) return true;
      return dispatch('updatePreferences', { mutedCourses: state.preferences.mutedCourses.filter(c => c !== id) });
    }
  },
  mutations: {
    SET_AVAILABLE (state, value) {
      state.available = value;
    },
    SET_PERMISSION (state, value) {
      state.permission = value || 'unknown';
    },
    SET_CHANNELS (state, value) {
      state.channels = {
        content: value && typeof value.content === 'boolean' ? value.content : null,
        replies: value && typeof value.replies === 'boolean' ? value.replies : null
      };
    },
    SET_PREFERENCES (state, { owner, preferences }) {
      state.preferences = preferences;
      state.confirmed = Object.assign({}, preferences, { mutedCourses: preferences.mutedCourses.slice() });
      state.preferencesOwner = owner;
      state.preferencesLoaded = true;
      state.preferencesError = false;
    },
    PATCH_PREFERENCES (state, patch) {
      Object.keys(patch).forEach(key => {
        Vue.set(state.preferences, key, key === 'mutedCourses' ? normalizeMuted(patch[key]) : patch[key]);
      });
    },
    CONFIRM_PREFERENCES (state, patch) {
      Object.keys(patch).forEach(key => {
        Vue.set(state.confirmed, key, key === 'mutedCourses' ? normalizeMuted(patch[key]) : patch[key]);
      });
    },
    SET_PREFERENCES_ERROR (state, value) {
      state.preferencesError = Boolean(value);
    },
    SET_SAVING (state, value) {
      state.savingPreferences = Boolean(value);
    },
    // Logout / troca de conta: nada do aluno anterior sobra na memória.
    RESET_PREFERENCES (state) {
      state.preferences = DEFAULT_PREFERENCES();
      state.confirmed = DEFAULT_PREFERENCES();
      state.preferencesOwner = null;
      state.preferencesLoaded = false;
      state.preferencesError = false;
    }
  }
};
